/**
 * Client-side SOL/USD price feed.
 *
 * Reads the server-proxied Jupiter quote from /api/public/sol-price and keeps
 * it fresh while a purchase widget is on screen. Falls back to the protocol
 * reference price so the widget always renders a usable quote.
 */
import { useCallback, useEffect, useRef, useState } from "react";

export const SOL_USD_FALLBACK = 150;
// Must stay well below DEFAULT_QUOTE_TTL_S so a live quote is refreshed
// before it can lapse.
const REFRESH_MS = 12_000;

export type SolPriceState = {
  /** USD per SOL. */
  price: number;
  /** "jupiter" | "jupiter-stale" | "reference" | "loading". */
  source: string;
  /** True until the first successful quote resolves. */
  loading: boolean;
  /** True when the widget is showing the offline reference price. */
  fallback: boolean;
  /**
   * Epoch ms of the last completed quote attempt. Always advances, even when
   * the price and source are numerically identical, so downstream memos
   * (e.g. the swap quote) rebuild and never get stuck past their expiry.
   */
  at: number;
  /** Force an immediate re-quote (used by the "refresh quote" control). */
  refresh: () => Promise<void>;
};

/** Convert a SOL amount into whole-cent USD value at the given rate. */
export function solToUsd(amount: number, price: number): number {
  if (!Number.isFinite(amount) || amount <= 0 || !Number.isFinite(price) || price <= 0) return 0;
  return Math.round(amount * price * 100) / 100;
}

export function useSolPrice(enabled = true): SolPriceState {
  const [state, setState] = useState<Omit<SolPriceState, "refresh">>({
    price: SOL_USD_FALLBACK,
    source: "loading",
    loading: true,
    fallback: true,
    at: Date.now(),
  });
  const loadRef = useRef<() => Promise<void>>(async () => undefined);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    const load = async () => {
      try {
        const res = await fetch("/api/public/sol-price", { headers: { Accept: "application/json" } });
        if (!res.ok) throw new Error(String(res.status));
        const body = (await res.json()) as { price?: number; source?: string };
        const price = Number(body?.price);
        if (cancelled || !Number.isFinite(price) || price <= 0) return;
        setState({
          price,
          source: body?.source ?? "jupiter",
          loading: false,
          fallback: (body?.source ?? "") === "reference",
          at: Date.now(),
        });
      } catch {
        if (cancelled) return;
        setState((s) => ({
          ...s,
          loading: false,
          source: "reference",
          fallback: true,
          at: Date.now(),
        }));
      }
    };

    loadRef.current = load;
    void load();
    const id = window.setInterval(() => void load(), REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [enabled]);

  const refresh = useCallback(() => loadRef.current(), []);

  return { ...state, refresh };
}
